/**
 * Traduce los errores de validación del backend a algo que el usuario pueda accionar.
 *
 * El backend manda dos formas: express-validator (`msg`/`param`) y los controllers que rebotan
 * un error de Sequelize (`message`/`field`). `api.js` ya las normaliza en `error.message` y
 * `error.field`; acá se convierte ese campo técnico en una frase en español.
 *
 * Vive aparte porque cuatro vistas (Citas, Calendario, Dashboard, Detalle de cliente) muestran
 * el mismo toast, y la traducción no puede estar copiada en cada una.
 */

/** Nombre del campo tal como lo ve el usuario en el formulario. */
const FIELD_LABELS: Record<string, string> = {
  phone: 'el teléfono del cliente',
  client_phone: 'el teléfono del cliente',
  clientPhone: 'el teléfono del cliente',
  full_name: 'el nombre del cliente',
  client_name: 'el nombre del cliente',
  clientName: 'el nombre del cliente',
  name: 'el nombre del cliente',
  email: 'el email del cliente',
  client_email: 'el email del cliente',
  service_id: 'el servicio',
  serviceId: 'el servicio',
  appointment_date: 'la fecha',
  appointment_time: 'la hora'
  // client_id NO lleva label: su 409 (doble reserva) ya trae el mensaje final del backend, y
  // agregarlo acá lo pisaría con "Falta el cliente." (ver validationMessage).
  // worker_id tampoco: ya no es obligatorio ("Sin asignar" es válido) y sus 409 de disponibilidad
  // ya traen el mensaje final armado — un label acá lo pisaría con "Falta el trabajador.".
}

/** Campo del formulario al que hay que llevar el cursor. Es la clave que usa el modal. */
const FIELD_TARGETS: Record<string, 'clientName' | 'phone' | 'email' | 'service' | 'date' | 'hour' | 'worker'> = {
  phone: 'phone',
  client_phone: 'phone',
  clientPhone: 'phone',
  full_name: 'clientName',
  client_name: 'clientName',
  clientName: 'clientName',
  name: 'clientName',
  email: 'email',
  client_email: 'email',
  service_id: 'service',
  serviceId: 'service',
  appointment_date: 'date',
  appointment_time: 'hour',
  worker_id: 'worker',
  // Doble reserva del cliente principal (POST /appointments 409): mismo campo que su nombre.
  client_id: 'clientName'
}

export type ValidationTarget = (typeof FIELD_TARGETS)[keyof typeof FIELD_TARGETS] | 'references'

/** Doble reserva (409, `references[i]`) o dato de un acompañante (422, `references[i].phone`). */
const REFERENCE_INDEX_FIELD = /^references\[(\d+)\](\.\w+)?$/

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

/** El campo que rebotó el backend, normalizado a la clave que entiende el formulario. */
export function validationTarget(error: unknown): ValidationTarget | null {
  const field = asRecord(error).field
  if (typeof field !== 'string') return null
  if (REFERENCE_INDEX_FIELD.test(field)) return 'references'
  return FIELD_TARGETS[field] ?? null
}

/** El índice `i` de `references[i]` / `references[i].phone`, o null si el campo no es de un acompañante. */
export function referenceIndex(error: unknown): number | null {
  const field = asRecord(error).field
  if (typeof field !== 'string') return null
  const match = field.match(REFERENCE_INDEX_FIELD)
  return match ? Number(match[1]) : null
}

/**
 * Mensaje listo para mostrar. Si el backend señaló un campo, se nombra en español; si no, se
 * devuelve el mensaje tal cual, que para errores no-de-validación ya viene redactado.
 *
 * El texto crudo del modelo ("Client.phone cannot be null") no se muestra nunca: no le dice
 * nada a quien está cargando una cita.
 */
export function validationMessage(error: unknown, fallback = 'Revisa los datos e intenta de nuevo'): string {
  const raw = asRecord(error)
  const field = typeof raw.field === 'string' ? raw.field : null
  const label = field ? FIELD_LABELS[field] : null
  const message = typeof raw.message === 'string' ? raw.message.trim() : ''

  // Un mensaje en inglés con notación de modelo es del ORM: no se le muestra al usuario.
  const isGeneric = !message || message === 'Error de validación' || /^[A-Z]\w+\.\w+ /.test(message)
  const isMissing = /requerid|obligatori|cannot be null/i.test(message)

  // "Falta …" solo cuando de verdad falta: un email inválido no es un email faltante.
  if (label && (isGeneric || isMissing)) return `Falta ${label}.`
  if (!isGeneric) return message
  return fallback
}
